import { Filter } from "matrix-js-sdk";
import { extractUrls, formatFileSize } from "./utils";
import { loadAuthenticatedMedia, downloadAuthenticatedFile } from "./media";
import { attachImageLightbox } from "./lightbox";
import { stripReplyFallback, formatSearchDate } from "./search_text";

// A room's media, links and documents — WhatsApp's "Medien, Links und Doks",
// opened from the chat details.

// tuwunel answers up to 1000 events per /messages request; half keeps a page quick.
const PAGE_LIMIT = 500;
// One batch loads until the open tab has this many more items…
const BATCH_ITEMS = 24;
// …or this many requests went out, so a room with none does not page through
// its whole history unasked.
const BATCH_REQUESTS = 5;

export const GALLERY_TABS = ["media", "links", "docs"];

const MONTH = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });
const BACK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>';

// Which tab a message belongs in, if any. Voice messages are conversation, not
// documents; edits are not messages of their own.
export function classifyMessage(content) {
  if (!content || content["m.relates_to"]?.rel_type === "m.replace") return null;
  switch (content.msgtype) {
    case "m.image":
    case "m.video":
      return content.url ? "media" : null;
    case "m.file":
      return content.url ? "docs" : null;
    case "m.audio":
      return content.url && content["org.matrix.msc3245.voice"] === undefined ? "docs" : null;
    case "m.text":
    case "m.notice":
      return extractUrls(stripReplyFallback(content)).length ? "links" : null;
    default:
      return null;
  }
}

// Walks a room's history from the newest message back, on requests of its
// own — the timeline on screen stays as it is — and sorts what it finds into
// the three tabs, newest first. One per room, kept while the chat runs.
export class RoomMediaScanner {
  constructor(client, room) {
    this.client = client;
    this.room = room;
    this.items = { media: [], links: [], docs: [] };
    this.seen = new Set();
    this.loading = null;
    this.failed = false;
    this.filter = Filter.fromJson(client.getUserId(), null, { room: { timeline: { types: ["m.room.message"] } } });
    // Where the timeline's own loading stopped; older than that is ours to fetch.
    this.token = room.getLiveTimeline().getPaginationToken("b") || null;
    this.addLoaded();
  }

  get exhausted() {
    return this.token === null;
  }

  // What the live timeline holds: on opening, and on every reopening, which
  // picks up the messages that arrived in between.
  addLoaded() {
    this.room.getLiveTimeline().getEvents().forEach((event) => this.add({
      eventId: event.getId(),
      sender: event.getSender(),
      ts: event.getTs(),
      type: event.getType(),
      content: event.getContent(),
      redacted: event.isRedacted()
    }));
    this.sort();
  }

  add({ eventId, sender, ts, type, content, redacted }) {
    if (type !== "m.room.message") return;
    if (redacted) {
      this.redact(eventId);
      return;
    }
    if (this.seen.has(eventId)) return;
    this.seen.add(eventId);

    const tab = classifyMessage(content);
    if (!tab) return;
    const item = { eventId, sender, ts, content };
    if (tab === "links") {
      extractUrls(stripReplyFallback(content)).forEach((url) => this.items.links.push({ ...item, url }));
    } else {
      this.items[tab].push(item);
    }
  }

  redact(eventId) {
    if (!eventId) return false;
    // Remember even an unseen id: a history request may still return the
    // original content after its redaction arrived through sync.
    this.seen.add(eventId);
    let removed = false;
    GALLERY_TABS.forEach((tab) => {
      const items = this.items[tab];
      this.items[tab] = items.filter((item) => item.eventId !== eventId);
      removed ||= this.items[tab].length !== items.length;
    });
    return removed;
  }

  sort() {
    GALLERY_TABS.forEach((tab) => this.items[tab].sort((a, b) => b.ts - a.ts));
  }

  // One batch at a time, for whichever tab asks first.
  loadMore(tab) {
    if (this.loading || this.exhausted) return this.loading || Promise.resolve();
    this.loading = this.loadBatch(tab).finally(() => { this.loading = null; });
    return this.loading;
  }

  async loadBatch(tab) {
    const goal = this.items[tab].length + BATCH_ITEMS;
    try {
      for (let request = 0; request < BATCH_REQUESTS && !this.exhausted && this.items[tab].length < goal; request++) {
        const response = await this.client.createMessagesRequest(this.room.roomId, this.token, PAGE_LIMIT, "b", this.filter);
        const chunk = response?.chunk || [];
        chunk.forEach((raw) => this.add({
          eventId: raw.event_id,
          sender: raw.sender,
          ts: raw.origin_server_ts,
          type: raw.type,
          content: raw.content,
          redacted: Boolean(raw.unsigned?.redacted_because)
        }));
        this.token = chunk.length && response.end ? response.end : null;
        this.sort();
      }
      this.failed = false;
    } catch (error) {
      console.error("[BryghtChat] Loading the room's media failed:", error);
      this.failed = true;
    }
  }
}

// The view: a header with the way back, three tabs, and the open tab's items
// grouped by month. Scrolling to the end loads the next batch.
export class MediaGallery {
  constructor(ui, room, scanner, { onBack }) {
    this.ui = ui;
    this.room = room;
    this.scanner = scanner;
    this.onBack = onBack;
    this.tab = "media";
    this.stalled = false;
    this.destroyed = false;

    this.element = this.build();
    this.renderTab();
    this.fill();
  }

  destroy() {
    this.destroyed = true;
    this.observer.disconnect();
  }

  t(key, fallback) {
    return this.ui.t(key, fallback);
  }

  build() {
    const element = el("div", "bryght-media-gallery");
    // Swipe from the left edge goes back to the details (swipe_back_controller.js).
    element.dataset.controller = "swipe-back";
    element.dataset.swipeBackSwipingClassValue = "is-swiping-media";

    const header = el("div", "bryght-chat-header");
    const back = el("button", "bryght-chat-back-button bryght-chat-back-button--visible");
    back.type = "button";
    back.title = this.t("back", "Zurück");
    back.innerHTML = BACK_ICON;
    back.addEventListener("click", () => this.onBack());
    header.append(back, el("h3", "bryght-media-gallery__title", this.t("gallery_title", "Medien, Links und Doks")));

    const tabs = el("div", "bryght-media-gallery__tabs");
    tabs.setAttribute("role", "tablist");
    const labels = {
      media: this.t("gallery_tab_media", "Medien"),
      links: this.t("gallery_tab_links", "Links"),
      docs: this.t("gallery_tab_docs", "Doks")
    };
    this.tabButtons = GALLERY_TABS.map((tab) => {
      const button = el("button", "bryght-media-gallery__tab", labels[tab]);
      button.type = "button";
      button.dataset.tab = tab;
      button.setAttribute("role", "tab");
      button.addEventListener("click", () => this.showTab(tab));
      tabs.appendChild(button);
      return button;
    });

    // Its own scroll container: ios_fixes.js only lets elements that scroll
    // themselves be dragged.
    this.body = el("div", "bryght-media-gallery__body");
    this.body.setAttribute("role", "tabpanel");
    // The lightbox swipes through the gallery's images, not the timeline's.
    this.body.dataset.lightboxScope = "";
    this.content = el("div", "bryght-media-gallery__content");
    this.status = el("div", "bryght-media-gallery__status");
    this.sentinel = el("div", "bryght-media-gallery__sentinel");
    this.body.append(this.content, this.status, this.sentinel);

    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.stalled) this.fill();
    }, { root: this.body, rootMargin: "0px 0px 200px 0px" });
    this.observer.observe(this.sentinel);

    element.append(header, tabs, this.body);
    return element;
  }

  showTab(tab) {
    if (tab === this.tab) return;
    this.tab = tab;
    this.stalled = false;
    this.body.scrollTop = 0;
    this.renderTab();
    if (this.scanner.items[tab].length < BATCH_ITEMS) this.fill();
  }

  async fill() {
    if (this.scanner.exhausted) {
      this.renderStatus();
      return;
    }

    const tab = this.tab;
    const before = this.scanner.items[tab].length;
    const pending = this.scanner.loadMore(tab);
    this.renderStatus();
    await pending;
    if (this.destroyed || tab !== this.tab) return;

    // A whole batch without anything for this tab: further loading waits for
    // the user to ask, rather than paging through a long text-only history.
    this.stalled = !this.scanner.exhausted && this.scanner.items[tab].length === before;
    this.renderTab();
    // The observer only reports changes; with the end still in sight, go on.
    if (!this.stalled && !this.scanner.failed && this.sentinelInView()) this.fill();
  }

  sentinelInView() {
    return this.sentinel.getBoundingClientRect().top <= this.body.getBoundingClientRect().bottom + 200;
  }

  renderTab() {
    this.tabButtons.forEach((button) => {
      const active = button.dataset.tab === this.tab;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-selected", String(active));
    });
    this.content.replaceChildren(...this.buildGroups(this.scanner.items[this.tab]));
    this.renderStatus();
  }

  renderStatus() {
    const { exhausted, failed, loading } = this.scanner;
    this.status.replaceChildren();

    if (loading) {
      this.status.textContent = this.t("gallery_loading", "Lade…");
    } else if (failed) {
      this.status.append(this.t("gallery_error", "Konnte nicht geladen werden."), " ",
        this.statusButton(this.t("gallery_retry", "Erneut versuchen")));
    } else if (this.stalled) {
      this.status.appendChild(this.statusButton(this.t("gallery_load_more", "Ältere laden")));
    } else if (exhausted && this.scanner.items[this.tab].length === 0) {
      const empty = {
        media: this.t("gallery_empty_media", "Noch keine Medien"),
        links: this.t("gallery_empty_links", "Noch keine Links"),
        docs: this.t("gallery_empty_docs", "Noch keine Dokumente")
      };
      this.status.textContent = empty[this.tab];
    }
  }

  statusButton(label) {
    const button = el("button", "bryght-media-gallery__more", label);
    button.type = "button";
    button.addEventListener("click", () => {
      this.stalled = false;
      this.fill();
    });
    return button;
  }

  buildGroups(items) {
    const nodes = [];
    let month = null;
    let group = null;
    for (const item of items) {
      const label = MONTH.format(new Date(item.ts));
      if (label !== month) {
        month = label;
        group = el("div", this.tab === "media" ? "bryght-media-gallery__grid" : "bryght-media-gallery__list");
        nodes.push(el("h4", "bryght-media-gallery__month", label), group);
      }
      group.appendChild(this.buildItem(item));
    }
    return nodes;
  }

  buildItem(item) {
    if (this.tab === "media") return this.buildTile(item);
    if (this.tab === "links") return this.buildLink(item);
    return this.buildDoc(item);
  }

  buildTile(item) {
    const { content } = item;
    const tile = el("div", "bryght-media-gallery__tile");

    if (content.msgtype === "m.image") {
      const img = document.createElement("img");
      img.alt = content.filename || content.body || this.t("image", "Bild");
      loadAuthenticatedMedia(content.url, img, "image", content.info?.mimetype);
      attachImageLightbox(img, content, this.t("image", "Bild"));
      tile.appendChild(img);
      return tile;
    }

    // Videos play in the chat; the tile takes you there.
    const button = el("button", "bryght-media-gallery__video");
    button.type = "button";
    button.setAttribute("aria-label", content.filename || content.body || "Video");
    const thumbnail = content.info?.thumbnail_url;
    if (thumbnail) {
      const img = document.createElement("img");
      img.alt = "";
      loadAuthenticatedMedia(thumbnail, img, "image", content.info?.thumbnail_info?.mimetype);
      button.appendChild(img);
    }
    button.appendChild(el("span", "bryght-media-gallery__play", "▶"));
    button.addEventListener("click", () => this.showInChat(item));
    tile.appendChild(button);
    return tile;
  }

  buildLink(item) {
    const link = document.createElement("a");
    link.className = "bryght-media-gallery__row-main";
    link.href = item.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.append(
      el("span", "bryght-media-gallery__row-icon", "🔗"),
      this.rowText(hostOf(item.url), item.url, item, "bryght-media-gallery__link-host")
    );
    return this.row(link, item);
  }

  buildDoc(item) {
    const { content } = item;
    const name = content.filename || content.body || this.t("file", "Datei");
    const download = el("button", "bryght-media-gallery__row-main");
    download.type = "button";
    download.append(
      el("span", "bryght-media-gallery__row-icon", "📎"),
      this.rowText(name, content.info?.size ? formatFileSize(content.info.size) : null, item)
    );
    download.addEventListener("click", () => downloadAuthenticatedFile(content.url, name));
    return this.row(download, item);
  }

  row(main, item) {
    const row = el("div", "bryght-media-gallery__row");
    const show = el("button", "bryght-media-gallery__show", this.t("gallery_show_in_chat", "Im Chat anzeigen"));
    show.type = "button";
    show.addEventListener("click", () => this.showInChat(item));
    row.append(main, show);
    return row;
  }

  rowText(title, detail, item, titleClass = "") {
    const text = el("span", "bryght-media-gallery__row-text");
    text.appendChild(el("span", `bryght-media-gallery__row-title ${titleClass}`.trim(), title));
    const sender = item.sender === this.ui.userId ? this.t("search_you", "Du") : this.ui.getUserInfo(item.sender).name;
    const meta = [detail, sender, formatSearchDate(item.ts)].filter(Boolean).join(" · ");
    text.appendChild(el("span", "bryght-media-gallery__row-meta", meta));
    return text;
  }

  showInChat(item) {
    this.ui.closeRoomDetails();
    this.ui.jumpToEvent(this.room, item.eventId, { ts: item.ts });
  }
}

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return url;
  }
}

function el(tag, className, text = null) {
  const element = document.createElement(tag);
  element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}
